/**
 *将数组数据转化成树形数据 
 */

import { deepClone } from './util'

/**
 * 快速浅拷贝（只拷贝第一层属性）
 * 性能远优于 deepClone，适用于不需要深层隔离的场景
 */
function shallowClone(obj) {
    if (!obj || typeof obj !== 'object') return obj
    const clone = Array.isArray(obj) ? [] : {}
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            clone[key] = obj[key]
        }
    }
    return clone
}

/**
 * 构建分组映射
 * 优化：使用 Set 替代数组查找，时间复杂度从 O(n) 降为 O(1)
 */
function setGroupMap(data, idKey, pidKey, idSet) {
    const groupList = {}
    const map = {}
    
    for (let i = 0, len = data.length; i < len; i++) {
        const item = data[i]
        const id = item[idKey]
        let parent = item[pidKey]
        
        // 使用 Set.has() 替代 arr.includes()，性能更优
        if (!idSet.has(parent)) parent = ''
        
        if (!groupList[parent]) {
            groupList[parent] = []
        }
        groupList[parent].push(item)
        map[id] = item
    }
    
    return { group: groupList, map: map }
}

/**
 * 递归构建树结构
 * 优化：使用 Set 替代数组 indexOf 检查
 */
function setTree(data, allList, idKey, valueKey, labelKey, filterSet) {
    const res = []
    
    if (data == null) {
        for (const id in allList.group) {
            // filterSet.has(id) 为 false 表示不过滤该节点
            if (!filterSet || !filterSet.has(id) || !id) {
                if (!allList.map[id]) {
                    const list = allList.group[id]
                    for (let i = 0, len = list.length; i < len; i++) {
                        const item2 = list[i]
                        if (!filterSet || !filterSet.has(item2[idKey])) {
                            if (valueKey) {
                                item2.value = item2[valueKey]
                            }
                            if (labelKey) {
                                item2.label = item2[labelKey]
                            }
                            
                            const children = allList.group[item2[idKey]]
                            if (children && children.length > 0) {
                                item2.children = setTree(children, allList, idKey, valueKey, labelKey, filterSet)
                            }
                            res.push(item2)
                        }
                    }
                }
            }
        }
    } else {
        for (let i = 0, len = data.length; i < len; i++) {
            const item = data[i]
            const itemId = item[idKey]
            if (!filterSet || !filterSet.has(itemId) || !itemId) {
                if (valueKey) {
                    item.value = item[valueKey]
                }
                if (labelKey) {
                    item.label = item[labelKey]
                }
                
                const children = allList.group[itemId]
                if (children && children.length > 0) {
                    item.children = setTree(children, allList, idKey, valueKey, labelKey, filterSet)
                }
                res.push(item)
            }
        }
    }

    return res
}

/**
 * 将数据转化成树形数据
 * 性能优化版本：
 * 1. 使用 Set 替代数组查找，查找复杂度从 O(n) 降为 O(1)
 * 2. 使用 for 循环替代 forEach，性能更好
 * 3. 仅在需要去重时进行拷贝，减少不必要的拷贝操作
 * 
 * @param {Array} data - 原始数据数组
 * @param {string} idKey - ID 字段名，默认 '_id'
 * @param {string} pidKey - 父ID 字段名，默认 '_pid'
 * @param {string} valueKey - value 字段名
 * @param {string} labelKey - label 字段名
 * @param {Array} filterIds - 需要过滤的 ID 列表
 * @returns {Array} 树形数据
 */
export function toTree(data, idKey, pidKey, valueKey, labelKey, filterIds) {
    // 参数默认值处理
    idKey = idKey || '_id'
    pidKey = pidKey || '_pid'
    
    // 空数据快速返回
    if (!data || !data.length) {
        return []
    }

    // 构建 filterIds 的 Set，用于快速查找
    const filterSet = filterIds && filterIds.length > 0 ? new Set(filterIds) : null
    
    // 特殊情况：idKey === pidKey 时，直接过滤返回
    if (idKey === pidKey) {
        const res = []
        for (let i = 0, len = data.length; i < len; i++) {
            const item = data[i]
            if (!filterSet || !filterSet.has(item[idKey])) {
                // 浅拷贝避免修改原数据
                const cloneItem = shallowClone(item)
                if (valueKey) {
                    cloneItem.value = item[valueKey]
                }
                if (labelKey) {
                    cloneItem.label = item[labelKey]
                }
                res.push(cloneItem)
            }
        }
        return res
    }

    // 数据去重（根据 valueKey）
    let _data
    let idSet
    
    if (valueKey) {
        // 需要去重时，使用 Map 去重并浅拷贝
        const uniqueMap = new Map()
        for (let i = 0, len = data.length; i < len; i++) {
            const item = data[i]
            const value = item[valueKey]
            if (!uniqueMap.has(value)) {
                // 使用浅拷贝替代深拷贝，大幅提升性能
                uniqueMap.set(value, shallowClone(item))
            }
        }
        _data = Array.from(uniqueMap.values())
        idSet = new Set(Array.from(uniqueMap.values(), item => item[idKey]))
    } else {
        // 不需要去重，直接使用原数据引用（不拷贝）
        _data = data
        idSet = new Set(Array.from(data, item => item[idKey]))
    }

    // 构建分组映射
    const allList = setGroupMap(_data, idKey, pidKey, idSet)
    
    // 构建树结构
    return setTree(null, allList, idKey, valueKey, labelKey, filterSet)
}


/**
 * 将树形数据扁平化为数组
 */
const setArray = (list, pid, data) => {
    data = data || []
    for (let i = 0, len = data.length; i < len; i++) {
        const item = data[i]
        
        // 如果已有 _id 和 _pid，保留它们（处理已扁平化的数据）
        if (item._id === undefined) {
            item._id = item.prop || `${pid}_${i}`
        }
        if (item._pid === undefined) {
            item._pid = pid
        }
        const children = item.children || []
        delete item.children
        list.push(item)

        if (children.length > 0) {
            item._hasChildren = true
            setArray(list, item._id, children)
        }
    }
}

export const toArray = (data) => {
    const list = []
    setArray(list, '0', deepClone(data))
    return list
}
